<script>
import { computed, defineAsyncComponent, useTemplateRef } from 'vue';
import { useStore } from 'vuex';
import { mapGetters } from 'vuex';
import { useAlert } from 'dashboard/composables';
import { useUISettings } from 'dashboard/composables/useUISettings';
import { useTrack } from 'dashboard/composables';
import keyboardEventListenerMixins from 'shared/mixins/keyboardEventListenerMixins';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';

import ReplyToMessage from './ReplyToMessage.vue';
import AttachmentPreview from 'dashboard/components/widgets/AttachmentsPreview.vue';
import ReplyTopPanel from 'dashboard/components/widgets/WootWriter/ReplyTopPanel.vue';
import ReplyEmailHead from './ReplyEmailHead.vue';
import ReplyBottomPanel from 'dashboard/components/widgets/WootWriter/ReplyBottomPanel.vue';
import ArticleSearchPopover from 'dashboard/routes/dashboard/helpcenter/components/ArticleSearch/SearchPopover.vue';
import MessageSignatureMissingAlert from './MessageSignatureMissingAlert.vue';
import ReplyBoxBanner from './ReplyBoxBanner.vue';
import JanelaDeResposta from './JanelaDeResposta.vue';
import { useJanelaDeResposta } from 'dashboard/composables/useJanelaDeResposta';
import Banner from 'dashboard/components/ui/Banner.vue';
import QuotedEmailPreview from './QuotedEmailPreview.vue';
import { REPLY_EDITOR_MODES } from 'dashboard/components/widgets/WootWriter/constants';
import WootMessageEditor from 'dashboard/components/widgets/WootWriter/Editor.vue';
import AudioRecorder from 'dashboard/components/widgets/WootWriter/AudioRecorder.vue';
import { AUDIO_FORMATS } from 'shared/constants/messages';
import { BUS_EVENTS } from 'shared/constants/busEvents';
import { CMD_AI_ASSIST } from 'dashboard/helper/commandbar/events';
import {
  getMessageVariables,
  getUndefinedVariablesInMessage,
} from '@chatwoot/utils';
import WhatsappTemplates from './WhatsappTemplates/Modal.vue';
import EnviarModelo from './EnviarModelo.vue';
import ContentTemplates from './ContentTemplates/ContentTemplatesModal.vue';
import PlaceInFollowUpModal from './PlaceInFollowUpModal.vue';
import ForwardContactModal from './ForwardContactModal.vue';
import { abrirGavetaDeAssinatura } from 'dashboard/components-next/assinatura/useGavetaDeAssinatura';
import { MESSAGE_MAX_LENGTH } from 'shared/helpers/MessageTypeHelper';
import inboxMixin, { INBOX_FEATURES } from 'shared/mixins/inboxMixin';
import { trimContent, debounce, getRecipients } from '@chatwoot/utils';
import wootConstants from 'dashboard/constants/globals';
import {
  extractQuotedEmailText,
  buildQuotedEmailHeader,
  truncatePreviewText,
  appendQuotedTextToMessage,
} from 'dashboard/helper/quotedEmailHelper';
import {
  CONVERSATION_EVENTS,
  CAPTAIN_EVENTS,
} from '../../../helper/AnalyticsHelper/events';
import fileUploadMixin from 'dashboard/mixins/fileUploadMixin';
import {
  appendSignature,
  removeSignature,
  getEffectiveChannelType,
} from 'dashboard/helper/editorHelper';
import { useCopilotReply } from 'dashboard/composables/useCopilotReply';
import { useKbd } from 'dashboard/composables/utils/useKbd';
import { isFileTypeAllowedForChannel } from 'shared/helpers/FileHelper';

import { LOCAL_STORAGE_KEYS } from 'dashboard/constants/localStorage';
import { LocalStorage } from 'shared/helpers/localStorage';
import { emitter } from 'shared/helpers/mitt';
const EmojiInput = defineAsyncComponent(
  () => import('shared/components/emoji/EmojiInput.vue')
);

export default {
  components: {
    ArticleSearchPopover,
    AttachmentPreview,
    AudioRecorder,
    ReplyBoxBanner,
    JanelaDeResposta,
    Banner,
    EmojiInput,
    MessageSignatureMissingAlert,
    ReplyBottomPanel,
    ReplyEmailHead,
    ReplyToMessage,
    ReplyTopPanel,
    ContentTemplates,
    WhatsappTemplates,
    EnviarModelo,
    PlaceInFollowUpModal,
    ForwardContactModal,
    WootMessageEditor,
    QuotedEmailPreview,
  },
  mixins: [inboxMixin, fileUploadMixin, keyboardEventListenerMixins],
  emits: ['toggleEditorSize'],
  setup() {
    const {
      uiSettings,
      isEditorHotKeyEnabled,
      fetchSignatureFlagFromUISettings,
      setQuotedReplyFlagForInbox,
      fetchQuotedReplyFlagFromUISettings,
    } = useUISettings();

    const replyEditor = useTemplateRef('replyEditor');
    const messageEditor = useTemplateRef('messageEditor');
    const copilot = useCopilotReply();
    const shortcutKey = useKbd(['$mod', '+', 'enter']);

    // Janela de 24 h do WhatsApp oficial, viva (fecha e reabre sem recarregar).
    const store = useStore();
    const conversaAberta = computed(() => store.getters.getSelectedChat);
    const caixaDaConversa = computed(() =>
      store.getters['inboxes/getInbox'](conversaAberta.value?.inbox_id)
    );
    const { aplica: janelaAplica, janela } = useJanelaDeResposta(
      conversaAberta,
      caixaDaConversa
    );

    return {
      uiSettings,
      isEditorHotKeyEnabled,
      fetchSignatureFlagFromUISettings,
      setQuotedReplyFlagForInbox,
      fetchQuotedReplyFlagFromUISettings,
      replyEditor,
      messageEditor,
      copilot,
      shortcutKey,
      janelaAplica,
      janela,
    };
  },
  data() {
    return {
      message: '',
      inReplyTo: {},
      isFocused: false,
      showEmojiPicker: false,
      attachedFiles: [],
      isRecordingAudio: false,
      recordingAudioState: '',
      recordingAudioDurationText: '',
      replyType: REPLY_EDITOR_MODES.REPLY,
      bccEmails: '',
      ccEmails: '',
      toEmails: '',
      doAutoSaveDraft: () => {},
      showWhatsAppTemplatesModal: false,
      mostrarEnviarModelo: false,
      showContentTemplatesModal: false,
      updateEditorSelectionWith: '',
      undefinedVariableMessage: '',
      showMentions: false,
      showUserMentions: false,
      showCannedMenu: false,
      showVariablesMenu: false,
      newConversationModalActive: false,
      showArticleSearchPopover: false,
      hasRecordedAudio: false,
      pendingSendAfterRecording: false,
      showFollowUpModal: false,
      showForwardContactModal: false,
      copilotAcceptedMessages: {},
      preLumosMessage: '',
      lumosFollowUpInput: '',
    };
  },
  computed: {
    ...mapGetters({
      currentChat: 'getSelectedChat',
      messageSignature: 'getMessageSignature',
      currentUser: 'getCurrentUser',
      lastEmail: 'getLastEmailInSelectedChat',
      globalConfig: 'globalConfig/get',
      accountId: 'getCurrentAccountId',
      isFeatureEnabledonAccount: 'accounts/isFeatureEnabledonAccount',
    }),
    currentContact() {
      const senderId = this.currentChat?.meta?.sender?.id;
      if (!senderId) return {};
      return this.$store.getters['contacts/getContact'](senderId);
    },
    shouldShowReplyToMessage() {
      return (
        this.inReplyTo?.id &&
        !this.isPrivate &&
        this.inboxHasFeature(INBOX_FEATURES.REPLY_TO) &&
        !this.is360DialogWhatsAppChannel &&
        !this.copilot.isGenerating.value
      );
    },
    showWhatsappTemplates() {
      // We support templates for API channels if someone updates templates manually via API
      // That's why we don't explicitly check for channel type here
      const templates = this.$store.getters['inboxes/getWhatsAppTemplates'](
        this.inboxId
      );
      // WhatsApp oficial pela WAME (25/09/2026): os modelos não ficam copiados
      // na caixa — vêm da Meta na hora (EnviarModelo). O ícone aparece sempre.
      return (
        (!!(templates && templates.length) || this.janelaAplica) &&
        !this.isPrivate
      );
    },
    showContentTemplates() {
      return this.isATwilioWhatsAppChannel && !this.isPrivate;
    },
    // Canal em que a resposta é livre e a JANELA é quem decide (não o "só nota"
    // do Chatwoot). `janelaAplica` vem da conversa: vale mesmo quando o cadastro
    // da caixa ainda não chegou ao cache do navegador (caixa nova, cache antigo).
    respostaPorJanela() {
      return this.isAWhatsAppChannel || this.isAPIInbox || this.janelaAplica;
    },
    isPrivate() {
      if (this.currentChat.can_reply || this.respostaPorJanela) {
        return this.isOnPrivateNote;
      }
      return true;
    },
    assignedAgent() {
      return this.currentChat?.meta?.assignee;
    },
    // Estilo Digisac: resposta pública só é liberada pro dono da conversa.
    // Sem assignee ainda (não atribuída a ninguém) NÃO bloqueia — só quando
    // já existe um responsável e não é o usuário logado.
    isAssignedToOtherAgent() {
      return (
        !!this.assignedAgent && this.assignedAgent.id !== this.currentUser.id
      );
    },
    composerBlockedByAssignment() {
      return this.isAssignedToOtherAgent && !this.isOnPrivateNote;
    },
    isReplyRestricted() {
      return !this.currentChat?.can_reply && !this.respostaPorJanela;
    },
    inboxId() {
      return this.currentChat.inbox_id;
    },
    inbox() {
      return this.$store.getters['inboxes/getInbox'](this.inboxId);
    },
    messagePlaceHolder() {
      if (this.composerBlockedByAssignment) {
        return this.$t('CONVERSATION.REPLYBOX.ASSIGNED_TO_OTHER_PLACEHOLDER', {
          name: this.assignedAgent?.name,
        });
      }
      if (this.isEditorDisabled) {
        if (this.janelaAplica) {
          return this.$t('CONVERSATION.JANELA.PLACEHOLDER');
        }
        if (this.isAWhatsAppChannel) {
          return this.$t('CONVERSATION.FOOTER.MESSAGING_RESTRICTED_WHATSAPP');
        }
        if (this.isAPIInbox) {
          return this.$t('CONVERSATION.FOOTER.MESSAGING_RESTRICTED_API');
        }
        return this.$t('CONVERSATION.FOOTER.MESSAGING_RESTRICTED');
      }
      if (this.isPrivate) {
        return this.$t('CONVERSATION.FOOTER.PRIVATE_MSG_INPUT');
      }

      // 🔴 O TEXTO DE AJUDA FALAVA DE UM TECLADO QUE O CELULAR NÃO TEM.
      //
      // "Shift + enter para nova linha. Digite '/' para selecionar uma
      // Resposta" tem 69 caracteres: num campo de 375px ele é cortado no meio
      // e ainda ensina um atalho que não existe no toque. Medido em
      // 08/09/2026 numa conversa real. No celular vira "Escreva uma
      // mensagem"; no desktop, onde o atalho existe e o campo é largo, o
      // texto completo continua.
      return window.matchMedia('(max-width: 639px)').matches
        ? this.$t('CONVERSATION.FOOTER.MSG_INPUT_MOBILE')
        : this.$t('CONVERSATION.FOOTER.MSG_INPUT');
    },
    isMessageLengthReachingThreshold() {
      return this.message.length > this.maxLength - 50;
    },
    charactersRemaining() {
      return this.maxLength - this.message.length;
    },
    isReplyButtonDisabled() {
      if (this.isEditorDisabled) return true;
      if (this.isATwitterInbox) return true;
      if (this.copilot.isGenerating.value) return true;
      if (this.hasAttachments || this.hasRecordedAudio) return false;

      return (
        this.isMessageEmpty ||
        this.message.length === 0 ||
        this.message.length > this.maxLength
      );
    },
    sender() {
      return {
        name: this.currentUser.name,
        thumbnail: this.currentUser.avatar_url,
      };
    },
    conversationType() {
      const { additional_attributes: additionalAttributes } = this.currentChat;
      const type = additionalAttributes ? additionalAttributes.type : '';
      return type || '';
    },
    maxLength() {
      if (this.isPrivate) {
        return MESSAGE_MAX_LENGTH.GENERAL;
      }
      if (this.isAFacebookInbox) {
        return MESSAGE_MAX_LENGTH.FACEBOOK;
      }
      if (this.isAnInstagramChannel) {
        return MESSAGE_MAX_LENGTH.INSTAGRAM;
      }
      if (this.isATelegramChannel) {
        return MESSAGE_MAX_LENGTH.TELEGRAM;
      }
      if (this.isATiktokChannel) {
        return MESSAGE_MAX_LENGTH.TIKTOK;
      }
      if (this.isATwilioWhatsAppChannel) {
        return MESSAGE_MAX_LENGTH.TWILIO_WHATSAPP;
      }
      if (this.isAWhatsAppCloudChannel) {
        return MESSAGE_MAX_LENGTH.WHATSAPP_CLOUD;
      }
      if (this.isASmsInbox) {
        return MESSAGE_MAX_LENGTH.TWILIO_SMS;
      }
      if (this.isAnEmailChannel) {
        return MESSAGE_MAX_LENGTH.EMAIL;
      }
      if (this.isATwilioSMSChannel) {
        return MESSAGE_MAX_LENGTH.TWILIO_SMS;
      }
      if (this.isAWhatsAppChannel) {
        return MESSAGE_MAX_LENGTH.WHATSAPP_CLOUD;
      }
      return MESSAGE_MAX_LENGTH.GENERAL;
    },
    showFileUpload() {
      return (
        this.isAWebWidgetInbox ||
        this.isAFacebookInbox ||
        this.isAWhatsAppChannel ||
        this.isAPIInbox ||
        this.isAnEmailChannel ||
        this.isASmsInbox ||
        this.isATelegramChannel ||
        this.isALineChannel ||
        this.isAnInstagramChannel ||
        this.isATiktokChannel
      );
    },
    replyButtonLabel() {
      let sendMessageText = this.$t('CONVERSATION.REPLYBOX.SEND');
      if (this.isPrivate) {
        sendMessageText = this.$t('CONVERSATION.REPLYBOX.CREATE');
      }
      const keyLabel = this.isEditorHotKeyEnabled('cmd_enter')
        ? `(${this.shortcutKey})`
        : '(↵)';
      return `${sendMessageText} ${keyLabel}`;
    },
    replyBoxClass() {
      return {
        'is-private': this.isPrivate,
        'is-focused': this.isFocused || this.hasAttachments,
        'is-compact': true,
      };
    },
    hasAttachments() {
      return this.attachedFiles.length;
    },
    hasMessageContent() {
      return trimContent(this.message).length > 0 || this.hasAttachments;
    },
    // O botão redondo da direita muda de papel: grava áudio com o campo vazio,
    // envia quando há texto ou gravação. O nome acompanha — era um botão sem
    // nome nenhum para leitor de tela, nos dois papéis.
    // 🔴 O MICROFONE SÓ APARECE ONDE SE PODE GRAVAR (16/09/2026).
    // Na nota privada — e em canal sem envio de arquivo — o gravador não
    // existe (`showAudioRecorder` é falso). Mesmo assim o botão mostrava o
    // microfone com o campo vazio, e o clique ligava `isRecordingAudio` sem
    // nada na tela: ao voltar para "Responder", o gravador surgia do nada.
    // Onde não se grava, o botão é o de enviar (desabilitado até ter texto).
    botaoGrava() {
      return (
        this.showAudioRecorder &&
        !this.showAudioRecorderEditor &&
        !this.hasMessageContent
      );
    },
    botaoPrincipalRotulo() {
      if (!this.botaoGrava) {
        return this.isPrivate
          ? this.$t('CONVERSATION.REPLYBOX.SAVE_NOTE')
          : this.$t('CONVERSATION.REPLYBOX.SEND');
      }
      return this.$t('CONVERSATION.REPLYBOX.RECORD_AUDIO');
    },
    showAudioRecorder() {
      return !this.isOnPrivateNote && this.showFileUpload;
    },
    showAudioRecorderEditor() {
      return this.showAudioRecorder && this.isRecordingAudio;
    },
    isOnPrivateNote() {
      return this.replyType === REPLY_EDITOR_MODES.NOTE;
    },
    isOnExpandedLayout() {
      const {
        LAYOUT_TYPES: { CONDENSED },
      } = wootConstants;
      const { conversation_display_type: conversationDisplayType = CONDENSED } =
        this.uiSettings;
      return conversationDisplayType !== CONDENSED;
    },
    isMessageEmpty() {
      if (!this.message) {
        return true;
      }
      return !this.message.trim().replace(/\n/g, '').length;
    },
    showReplyHead() {
      return !this.isOnPrivateNote && this.isAnEmailChannel;
    },
    enableMultipleFileUpload() {
      return (
        this.isAnEmailChannel ||
        this.isAWebWidgetInbox ||
        this.isAPIInbox ||
        this.isAWhatsAppChannel ||
        this.isATelegramChannel
      );
    },
    isSignatureEnabledForInbox() {
      return !this.isPrivate && this.sendWithSignature;
    },
    isSignatureAvailable() {
      return !!this.messageSignature;
    },
    sendWithSignature() {
      return this.fetchSignatureFlagFromUISettings(this.channelType);
    },
    conversationId() {
      return this.currentChat.id;
    },
    conversationIdByRoute() {
      return this.conversationId;
    },
    editorStateId() {
      return `draft-${this.conversationIdByRoute}-${this.replyType}`;
    },
    audioRecordFormat() {
      if (this.isAWhatsAppChannel || this.isATelegramChannel) {
        return AUDIO_FORMATS.MP3;
      }
      if (this.isAPIInbox) {
        return AUDIO_FORMATS.MP3;
      }
      return AUDIO_FORMATS.WAV;
    },
    messageVariables() {
      const variables = getMessageVariables({
        conversation: this.currentChat,
        contact: this.currentContact,
        inbox: this.inbox,
      });
      return variables;
    },
    connectedPortalSlug() {
      const { help_center: portal = {} } = this.inbox;
      const { slug = '' } = portal;
      return slug;
    },
    isQuotedEmailReplyEnabled() {
      return this.isFeatureEnabledonAccount(
        this.accountId,
        FEATURE_FLAGS.QUOTED_EMAIL_REPLY
      );
    },
    quotedReplyPreference() {
      if (!this.isAnEmailChannel || !this.isQuotedEmailReplyEnabled) {
        return false;
      }

      return !!this.fetchQuotedReplyFlagFromUISettings(this.channelType);
    },
    lastEmailWithQuotedContent() {
      if (!this.isAnEmailChannel) {
        return null;
      }

      const lastEmail = this.lastEmail;
      if (!lastEmail || lastEmail.private) {
        return null;
      }

      return lastEmail;
    },
    quotedEmailText() {
      return extractQuotedEmailText(this.lastEmailWithQuotedContent);
    },
    quotedEmailPreviewText() {
      return truncatePreviewText(this.quotedEmailText, 80);
    },
    shouldShowQuotedReplyToggle() {
      return (
        this.isAnEmailChannel &&
        !this.isOnPrivateNote &&
        this.isQuotedEmailReplyEnabled
      );
    },
    shouldShowQuotedPreview() {
      return (
        this.shouldShowQuotedReplyToggle &&
        this.quotedReplyPreference &&
        !!this.quotedEmailText
      );
    },
    isDefaultEditorMode() {
      return !this.showAudioRecorderEditor;
    },
    // Na caixa do WhatsApp oficial vale o relógio da tela (fecha na hora exata e
    // reabre quando o cliente escreve); nas outras, o `can_reply` do servidor.
    podeResponderLivre() {
      if (this.janelaAplica) return this.janela.estado !== 'fechada';
      return this.currentChat.can_reply;
    },
    mostrarJanela() {
      return (
        this.janelaAplica &&
        !this.isOnPrivateNote &&
        this.janela.estado !== 'aberta'
      );
    },
    isEditorDisabled() {
      if (this.composerBlockedByAssignment) return true;

      return (
        this.respostaPorJanela &&
        !this.isOnPrivateNote &&
        !this.podeResponderLivre
      );
    },
  },
  watch: {
    currentChat(conversation, oldConversation) {
      const { can_reply: canReply } = conversation;
      if (oldConversation && oldConversation.id !== conversation.id) {
        // Only update email fields when switching to a completely different conversation (by ID)
        // This prevents overwriting user input (e.g., CC/BCC fields) when performing actions
        // like self-assign or other updates that do not actually change the conversation context
        this.setCCAndToEmailsFromLastChat();
        // Reset Copilot editor state (includes cancelling ongoing generation)
        this.copilot.reset();
      }

      if (this.isOnPrivateNote) {
        return;
      }

      if (canReply || this.respostaPorJanela) {
        this.replyType = REPLY_EDITOR_MODES.REPLY;
      } else {
        this.replyType = REPLY_EDITOR_MODES.NOTE;
      }

      this.fetchAndSetReplyTo();
    },
    // When moving from one conversation to another, the store may not have the
    // list of all the messages. A fetch is subsequently made to get the messages.
    // This watcher handles two main cases:
    // 1. When switching conversations and messages are fetched/updated, ensures CC/BCC fields are set from the latest OUTGOING/INCOMING email (not activity/private messages).
    // 2. Fixes and issue where CC/BCC fields could be reset/lost after assignment/activity actions or message mutations that did not represent a true email context change.
    lastEmail: {
      handler(lastEmail) {
        if (!lastEmail) return;
        this.setCCAndToEmailsFromLastChat();
      },
      deep: true,
    },
    conversationIdByRoute(conversationId, oldConversationId) {
      if (conversationId !== oldConversationId) {
        this.setToDraft(oldConversationId, this.replyType);
        this.getFromDraft();
        this.resetRecorderAndClearAttachments();
      }
    },
    message() {
      // Autosave the current message draft.
      this.doAutoSaveDraft();
    },
    replyType(updatedReplyType, oldReplyType) {
      this.setToDraft(this.conversationIdByRoute, oldReplyType);
      this.getFromDraft();
    },
  },

  mounted() {
    this.getFromDraft();
    // Don't use the keyboard listener mixin here as the events here are supposed to be
    // working even if the editor is focussed.
    document.addEventListener('paste', this.onPaste);
    document.addEventListener('keydown', this.handleKeyEvents);
    this.setCCAndToEmailsFromLastChat();
    this.doAutoSaveDraft = debounce(
      () => {
        this.saveDraft(this.conversationIdByRoute, this.replyType);
      },
      500,
      true
    );

    this.fetchAndSetReplyTo();
    emitter.on(BUS_EVENTS.TOGGLE_REPLY_TO_MESSAGE, this.onReplyToMessage);

    // A hacky fix to solve the drag and drop
    // Is showing on top of new conversation modal drag and drop
    // TODO need to find a better solution
    emitter.on(
      BUS_EVENTS.NEW_CONVERSATION_MODAL,
      this.onNewConversationModalActive
    );
    emitter.on(BUS_EVENTS.INSERT_INTO_NORMAL_EDITOR, this.addIntoEditor);
    emitter.on(CMD_AI_ASSIST, this.executeCopilotAction);

    this.$watch(
      () => this.copilot.generatedContent.value,
      newContent => {
        if (newContent && !this.copilot.isGenerating.value) {
          this.message = newContent;
          this.setCopilotAcceptedMessage(newContent);
          this.copilot.setContentReady();
        }
      }
    );
  },
  unmounted() {
    document.removeEventListener('paste', this.onPaste);
    document.removeEventListener('keydown', this.handleKeyEvents);
    emitter.off(BUS_EVENTS.TOGGLE_REPLY_TO_MESSAGE, this.onReplyToMessage);
    emitter.off(BUS_EVENTS.INSERT_INTO_NORMAL_EDITOR, this.addIntoEditor);
    emitter.off(
      BUS_EVENTS.NEW_CONVERSATION_MODAL,
      this.onNewConversationModalActive
    );
    emitter.off(CMD_AI_ASSIST, this.executeCopilotAction);
  },
  methods: {
    getDraftKey(
      conversationId = this.conversationIdByRoute,
      replyType = this.replyType
    ) {
      return `draft-${conversationId}-${replyType}`;
    },
    getCopilotAcceptedMessage(replyType = this.replyType) {
      const key = this.getDraftKey(this.conversationIdByRoute, replyType);
      return this.copilotAcceptedMessages[key] || '';
    },
    setCopilotAcceptedMessage(message, replyType = this.replyType) {
      const key = this.getDraftKey(this.conversationIdByRoute, replyType);
      this.copilotAcceptedMessages[key] = trimContent(
        message || '',
        this.maxLength
      );
    },
    clearCopilotAcceptedMessage(replyType = this.replyType) {
      const key = this.getDraftKey(this.conversationIdByRoute, replyType);
      delete this.copilotAcceptedMessages[key];
    },
    handleInsert(article) {
      const { url, title } = article;
      // Removing empty lines from the title
      const lines = title.split('\n');
      const nonEmptyLines = lines.filter(line => line.trim() !== '');
      const filteredMarkdown = nonEmptyLines.join(' ');
      emitter.emit(
        BUS_EVENTS.INSERT_INTO_RICH_EDITOR,
        `[${filteredMarkdown}](${url})`
      );

      useTrack(CONVERSATION_EVENTS.INSERT_ARTICLE_LINK);
    },
    toggleQuotedReply() {
      if (!this.isAnEmailChannel) {
        return;
      }

      const nextValue = !this.quotedReplyPreference;
      this.setQuotedReplyFlagForInbox(this.channelType, nextValue);
    },
    openFollowUpModal() {
      this.$nextTick(() => {
        this.$refs.followUpModal?.open();
      });
      this.showFollowUpModal = true;
    },
    closeFollowUpModal() {
      this.showFollowUpModal = false;
    },
    abrirAssinatura() {
      abrirGavetaDeAssinatura(this.conversationId);
    },
    openForwardContactModal() {
      this.showForwardContactModal = true;
    },
    closeForwardContactModal() {
      this.showForwardContactModal = false;
    },
    shouldIncludeQuotedEmail() {
      return (
        this.isQuotedEmailReplyEnabled &&
        this.quotedReplyPreference &&
        this.shouldShowQuotedReplyToggle &&
        !!this.quotedEmailText
      );
    },
    getMessageWithQuotedEmailText(message) {
      if (!this.shouldIncludeQuotedEmail()) {
        return message;
      }

      const quotedText = this.quotedEmailText || '';
      const header = buildQuotedEmailHeader(
        this.lastEmailWithQuotedContent,
        this.currentContact,
        this.inbox
      );

      return appendQuotedTextToMessage(message, quotedText, header);
    },
    resetRecorderAndClearAttachments() {
      // Reset audio recorder UI state
      this.resetAudioRecorderInput();
      // Reset attached files
      this.attachedFiles = [];
    },
    saveDraft(conversationId, replyType) {
      if (this.message || this.message === '') {
        const key = this.getDraftKey(conversationId, replyType);
        const draftToSave = trimContent(this.message || '', this.maxLength);

        this.$store.dispatch('draftMessages/set', {
          key,
          message: draftToSave,
        });
      }
    },
    setToDraft(conversationId, replyType) {
      this.saveDraft(conversationId, replyType);
      this.message = '';
    },
    getFromDraft() {
      if (this.conversationIdByRoute) {
        const key = this.getDraftKey();
        const messageFromStore =
          this.$store.getters['draftMessages/get'](key) || '';

        // ensure that the message has signature set based on the ui setting
        this.message = this.toggleSignatureForDraft(messageFromStore);
      }
    },
    toggleSignatureForDraft(message) {
      if (this.isPrivate) {
        return message;
      }

      // Even when editor is disabled (e.g. WhatsApp/API can't reply), we must
      // still normalize stale signatures out of drafts when signature is off.
      if (this.isEditorDisabled && this.sendWithSignature) {
        return message;
      }

      const effectiveChannelType = getEffectiveChannelType(
        this.channelType,
        this.inbox?.medium || ''
      );

      return this.sendWithSignature
        ? appendSignature(message, this.messageSignature, effectiveChannelType)
        : removeSignature(message, this.messageSignature, effectiveChannelType);
    },
    removeFromDraft() {
      if (this.conversationIdByRoute) {
        const key = this.getDraftKey();
        this.$store.dispatch('draftMessages/delete', { key });
      }
    },
    getElementToBind() {
      return this.replyEditor;
    },
    getKeyboardEvents() {
      return {
        Escape: {
          action: () => {
            this.hideEmojiPicker();
          },
          allowOnFocusedInput: true,
        },
        '$mod+KeyK': {
          action: e => {
            e.preventDefault();
            const ninja = document.querySelector('ninja-keys');
            ninja.open();
          },
          allowOnFocusedInput: true,
        },
        Enter: {
          action: e => {
            if (this.isAValidEvent('enter')) {
              this.onSendReply();
              e.preventDefault();
            }
          },
          allowOnFocusedInput: true,
        },
        '$mod+Enter': {
          action: () => {
            if (this.isAValidEvent('cmd_enter')) {
              this.onSendReply();
            }
          },
          allowOnFocusedInput: true,
        },
      };
    },
    isAValidEvent(selectedKey) {
      return (
        !this.showUserMentions &&
        !this.showMentions &&
        !this.showCannedMenu &&
        !this.showVariablesMenu &&
        this.isFocused &&
        this.isEditorHotKeyEnabled(selectedKey)
      );
    },
    onPaste(e) {
      // Don't handle paste if compose new conversation modal is open
      if (this.newConversationModalActive) return;

      // Don't handle paste if editor is disabled
      if (this.isEditorDisabled) return;

      // Filter valid files (non-zero size)
      Array.from(e.clipboardData.files)
        .filter(file => file.size > 0)
        .filter(file => {
          const isAllowed = isFileTypeAllowedForChannel(file, {
            channelType: this.channelType || this.inbox?.channel_type,
            medium: this.inbox?.medium,
            conversationType: this.conversationType,
            isInstagramChannel: this.isAnInstagramChannel,
            isOnPrivateNote: this.isOnPrivateNote,
          });

          if (!isAllowed) {
            useAlert(
              this.$t('CONVERSATION.FILE_TYPE_NOT_SUPPORTED', {
                fileName: file.name,
              })
            );
          }

          return isAllowed;
        })
        .forEach(file => {
          const { name, type, size } = file;
          this.onFileUpload({ name, type, size, file });
        });
    },
    toggleUserMention(currentMentionState) {
      this.showUserMentions = currentMentionState;
    },
    toggleCannedMenu(value) {
      this.showCannedMenu = value;
    },
    toggleVariablesMenu(value) {
      this.showVariablesMenu = value;
    },
    openWhatsappTemplateModal() {
      if (this.janelaAplica) {
        this.mostrarEnviarModelo = true;
        return;
      }
      this.showWhatsAppTemplatesModal = true;
    },
    hideWhatsappTemplatesModal() {
      this.showWhatsAppTemplatesModal = false;
    },
    openContentTemplateModal() {
      this.showContentTemplatesModal = true;
    },
    hideContentTemplatesModal() {
      this.showContentTemplatesModal = false;
    },
    confirmOnSendReply() {
      if (this.isReplyButtonDisabled) {
        return;
      }
      if (!this.showMentions) {
        const copilotAcceptedMessage = this.getCopilotAcceptedMessage();
        const isOnWhatsApp =
          this.isATwilioWhatsAppChannel ||
          this.isAWhatsAppCloudChannel ||
          this.is360DialogWhatsAppChannel;
        // Instagram and TikTok do not support sending text and attachments in the same message.
        // For Instagram, combining them causes duplicate messages due to separate echo events per component.
        // For TikTok, the API rejects messages that mix text and media.
        // To handle both cases, text and attachments are always sent as separate messages.
        const isOnInstagram = this.isAnInstagramChannel;
        const isOnTiktok = this.isATiktokChannel;
        if ((isOnWhatsApp || isOnInstagram || isOnTiktok) && !this.isPrivate) {
          this.sendMessageAsMultipleMessages(
            this.message,
            copilotAcceptedMessage
          );
        } else {
          const messagePayload = this.getMessagePayload(this.message);
          this.sendMessage(
            messagePayload,
            this.message,
            copilotAcceptedMessage
          );
        }

        if (!this.isPrivate) {
          this.clearEmailField();
        }

        this.clearMessage();
        this.hideEmojiPicker();
      }
    },
    sendMessageAsMultipleMessages(message, copilotAcceptedMessage = '') {
      const messages = this.getMultipleMessagesPayload(message);
      messages.forEach(messagePayload => {
        this.sendMessage(
          messagePayload,
          messagePayload.message || '',
          copilotAcceptedMessage
        );
      });
    },
    sendMessageAnalyticsData(
      isPrivate,
      { editorMessage = '', copilotAcceptedMessage = '' } = {}
    ) {
      const normalizeForComparison = message => {
        let normalizedMessage = message || '';

        if (this.sendWithSignature && this.messageSignature && !isPrivate) {
          const effectiveChannelType = getEffectiveChannelType(
            this.channelType,
            this.inbox?.medium || ''
          );
          normalizedMessage = removeSignature(
            normalizedMessage,
            this.messageSignature,
            effectiveChannelType
          );
        }

        return trimContent(normalizedMessage);
      };

      const normalizedAcceptedMessage = normalizeForComparison(
        copilotAcceptedMessage
      );
      const normalizedEditorMessage = normalizeForComparison(editorMessage);

      if (normalizedAcceptedMessage && normalizedEditorMessage) {
        useTrack(CAPTAIN_EVENTS.AI_ASSISTED_MESSAGE_SENT, {
          conversationId: this.conversationIdByRoute,
          channelType: this.channelType,
          editedBeforeSend:
            normalizedAcceptedMessage !== normalizedEditorMessage,
          isPrivate,
        });
      }

      // Analytics data for message signature is enabled or not in channels
      return isPrivate
        ? useTrack(CONVERSATION_EVENTS.SENT_PRIVATE_NOTE)
        : useTrack(CONVERSATION_EVENTS.SENT_MESSAGE, {
            channelType: this.channelType,
            signatureEnabled: this.sendWithSignature,
            hasReplyTo: !!this.inReplyTo?.id,
          });
    },
    async onSendReply() {
      const undefinedVariables = getUndefinedVariablesInMessage({
        message: this.message,
        variables: this.messageVariables,
      });
      if (undefinedVariables.length > 0) {
        const undefinedVariablesCount =
          undefinedVariables.length > 1 ? undefinedVariables.length : 1;
        this.undefinedVariableMessage = this.$t(
          'CONVERSATION.REPLYBOX.UNDEFINED_VARIABLES.MESSAGE',
          {
            undefinedVariablesCount,
            undefinedVariables: undefinedVariables.join(', '),
          }
        );

        const ok = await this.$refs.confirmDialog.showConfirmation();
        if (ok) {
          this.confirmOnSendReply();
        }
      } else {
        this.confirmOnSendReply();
      }
    },
    async sendMessage(
      messagePayload,
      editorMessage = '',
      copilotAcceptedMessage = ''
    ) {
      try {
        await this.$store.dispatch(
          'createPendingMessageAndSend',
          messagePayload
        );
        emitter.emit(BUS_EVENTS.SCROLL_TO_MESSAGE);
        emitter.emit(BUS_EVENTS.MESSAGE_SENT);
        this.removeFromDraft();
        this.sendMessageAnalyticsData(messagePayload.private, {
          editorMessage,
          copilotAcceptedMessage,
        });
      } catch (error) {
        const errorMessage =
          error?.response?.data?.error || this.$t('CONVERSATION.MESSAGE_ERROR');
        useAlert(errorMessage);
      }
    },
    async onSendWhatsAppReply(messagePayload) {
      this.sendMessage({
        conversationId: this.currentChat.id,
        ...messagePayload,
      });
      this.hideWhatsappTemplatesModal();
    },
    async onSendContentTemplateReply(messagePayload) {
      this.sendMessage({
        conversationId: this.currentChat.id,
        ...messagePayload,
      });
      this.hideContentTemplatesModal();
    },
    switchToPrivateNote() {
      this.setReplyMode(REPLY_EDITOR_MODES.NOTE);
    },
    async transferConversationToMe() {
      try {
        const conversationId = this.currentChat.id;
        // Chama a API real primeiro. O update otimista ANTES do await
        // (padrão do ReplyBoxBanner.vue) corre risco de um refresh de lista
        // concorrente (poll/websocket) sobrescrever `meta.assignee` de volta
        // pro valor antigo enquanto o await roda — aplicar por ÚLTIMO garante
        // que a nossa mudança é a que vale no final.
        await this.$store.dispatch('assignAgent', {
          conversationId,
          agentId: this.currentUser.id,
        });
        const { avatar_url: avatarUrl, ...rest } = this.currentUser || {};
        this.$store.dispatch('setCurrentChatAssignee', {
          conversationId,
          assignee: { ...rest, thumbnail: avatarUrl },
        });
        useAlert(this.$t('CONVERSATION.CHANGE_AGENT'));
      } catch (error) {
        useAlert(this.$t('CONVERSATION.CHANGE_AGENT_FAILED'));
      }
    },
    setReplyMode(mode = REPLY_EDITOR_MODES.REPLY) {
      // Clear attachments when switching between private note and reply modes
      // This is to prevent from breaking the upload rules
      if (this.attachedFiles.length > 0) this.attachedFiles = [];

      const { can_reply: canReply } = this.currentChat;
      this.$store.dispatch('draftMessages/setReplyEditorMode', {
        mode,
      });
      if (canReply || this.respostaPorJanela) this.replyType = mode;
      if (this.isRecordingAudio) {
        this.toggleAudioRecorder();
      }
    },
    clearEditorSelection() {
      this.updateEditorSelectionWith = '';
    },
    addIntoEditor(content) {
      this.updateEditorSelectionWith = content;
      this.onFocus();
    },
    executeCopilotAction(action, data) {
      this.preLumosMessage = this.message;
      this.copilot.execute(action, data);
    },
    onLumosDiscard() {
      this.message = this.preLumosMessage;
      this.preLumosMessage = '';
      this.lumosFollowUpInput = '';
      this.clearCopilotAcceptedMessage();
      this.copilot.reset(false);
    },
    onLumosFollowUp() {
      const msg = this.lumosFollowUpInput.trim();
      if (!msg) return;
      this.lumosFollowUpInput = '';
      this.copilot.sendFollowUp(msg);
    },
    clearMessage() {
      this.message = '';
      this.clearCopilotAcceptedMessage();
      if (this.copilot.showEditor.value || this.copilot.isGenerating.value) {
        this.copilot.reset(false);
      }
      this.preLumosMessage = '';
      this.lumosFollowUpInput = '';
      if (this.sendWithSignature && !this.isPrivate) {
        // if signature is enabled, append it to the message
        const effectiveChannelType = getEffectiveChannelType(
          this.channelType,
          this.inbox?.medium || ''
        );
        this.message = appendSignature(
          this.message,
          this.messageSignature,
          effectiveChannelType
        );
      }
      this.attachedFiles = [];
      this.isRecordingAudio = false;
      this.resetReplyToMessage();
      this.resetAudioRecorderInput();
    },
    clearEmailField() {
      this.ccEmails = '';
      this.bccEmails = '';
      this.toEmails = '';
    },

    toggleEmojiPicker() {
      this.showEmojiPicker = !this.showEmojiPicker;
    },
    toggleAudioRecorder() {
      this.isRecordingAudio = !this.isRecordingAudio;
      if (!this.isRecordingAudio) {
        this.resetAudioRecorderInput();
      }
    },
    toggleAudioRecorderPlayPause() {
      if (!this.$refs.audioRecorderInput) return;
      if (!this.recordingAudioState) {
        this.$refs.audioRecorderInput.stopRecording();
      } else {
        this.$refs.audioRecorderInput.playPause();
      }
    },
    hideEmojiPicker() {
      if (this.showEmojiPicker) {
        this.toggleEmojiPicker();
      }
    },
    onTypingOn() {
      this.toggleTyping('on');
    },
    onTypingOff() {
      this.toggleTyping('off');
    },
    onBlur() {
      this.isFocused = false;
      this.saveDraft(this.conversationIdByRoute, this.replyType);
    },
    onFocus() {
      this.isFocused = true;
    },
    onRecordProgressChanged(duration) {
      this.recordingAudioDurationText = duration;
    },
    onFinishRecorder(file) {
      this.recordingAudioState = 'stopped';
      this.hasRecordedAudio = true;
      // Added a new key isRecordedAudio to the file to find it's and recorded audio
      // Because to filter and show only non recorded audio and other attachments
      const autoRecordedFile = {
        ...file,
        isRecordedAudio: true,
      };
      if (this.pendingSendAfterRecording) {
        this.pendingSendAfterRecording = false;
        // Push directly without FileReader (audio needs no thumbnail preview).
        // FileReader is async so $nextTick fires before the file lands in attachedFiles.
        this.attachedFiles = [
          ...this.attachedFiles,
          {
            currentChatId: this.currentChat.id,
            resource: autoRecordedFile,
            isPrivate: this.isPrivate,
            thumb: null,
            blobSignedId: undefined,
            isRecordedAudio: true,
          },
        ];
        this.$nextTick(() => this.onSendReply());
        return undefined;
      }
      return file && this.onFileUpload(autoRecordedFile);
    },
    stopAndSendRecording() {
      this.pendingSendAfterRecording = true;
      // Call stopRecording() on the ref directly — this emits record-end → onFinishRecorder.
      // toggleAudioRecorder() would set isRecordingAudio=false immediately, destroying the
      // v-if component before WaveSurfer can emit record-end and deliver the blob.
      if (this.$refs.audioRecorderInput) {
        this.$refs.audioRecorderInput.stopRecording();
      }
    },
    toggleTyping(status) {
      const conversationId = this.currentChat.id;
      const isPrivate = this.isPrivate;

      if (!conversationId) {
        return;
      }

      // "Digitando…" é aviso de cortesia e sai a cada pausa na digitação:
      // com a rede instável virava uma rejeição solta por tecla.
      this.$store
        .dispatch('conversationTypingStatus/toggleTyping', {
          status,
          conversationId,
          isPrivate,
        })
        .catch(() => {});
    },
    attachFile({ blob, file }) {
      const reader = new FileReader();
      reader.readAsDataURL(file.file);
      reader.onloadend = () => {
        this.attachedFiles.push({
          currentChatId: this.currentChat.id,
          resource: blob || file,
          isPrivate: this.isPrivate,
          thumb: reader.result,
          blobSignedId: blob ? blob.signed_id : undefined,
          isRecordedAudio: file?.isRecordedAudio || false,
        });
      };
    },
    removeAttachment(attachments) {
      this.attachedFiles = attachments;
    },
    setReplyToInPayload(payload) {
      if (this.inReplyTo?.id) {
        return {
          ...payload,
          contentAttributes: {
            ...payload.contentAttributes,
            in_reply_to: this.inReplyTo.id,
          },
        };
      }

      return payload;
    },
    getMultipleMessagesPayload(message) {
      const multipleMessagePayload = [];

      if (this.attachedFiles && this.attachedFiles.length) {
        let caption =
          this.isAnInstagramChannel || this.isATiktokChannel ? '' : message;
        this.attachedFiles.forEach(attachment => {
          const attachedFile = this.globalConfig.directUploadsEnabled
            ? attachment.blobSignedId
            : attachment.resource.file;
          let attachmentPayload = {
            conversationId: this.currentChat.id,
            files: [attachedFile],
            private: false,
            message: caption,
            sender: this.sender,
          };

          attachmentPayload = this.setReplyToInPayload(attachmentPayload);
          multipleMessagePayload.push(attachmentPayload);
          // For WhatsApp, only the first attachment gets a caption
          if (!this.isAnInstagramChannel) caption = '';
        });
      }

      const hasNoAttachments =
        !this.attachedFiles || !this.attachedFiles.length;
      // For Instagram and TikTok, text must always be sent as a separate message (no captions on attachments).
      // For WhatsApp, we only need a text message if there are no attachments.
      if (
        ((this.isAnInstagramChannel || this.isATiktokChannel) &&
          this.message) ||
        (!(this.isAnInstagramChannel || this.isATiktokChannel) &&
          hasNoAttachments)
      ) {
        let messagePayload = {
          conversationId: this.currentChat.id,
          message,
          private: false,
          sender: this.sender,
        };

        messagePayload = this.setReplyToInPayload(messagePayload);

        multipleMessagePayload.push(messagePayload);
      }

      return multipleMessagePayload;
    },
    getMessagePayload(message) {
      const messageWithQuote = this.getMessageWithQuotedEmailText(message);

      let messagePayload = {
        conversationId: this.currentChat.id,
        message: messageWithQuote,
        private: this.isPrivate,
        sender: this.sender,
      };
      messagePayload = this.setReplyToInPayload(messagePayload);

      if (this.attachedFiles && this.attachedFiles.length) {
        messagePayload.files = [];
        this.attachedFiles.forEach(attachment => {
          if (this.globalConfig.directUploadsEnabled) {
            messagePayload.files.push(attachment.blobSignedId);
          } else {
            messagePayload.files.push(attachment.resource.file);
          }
        });
      }

      if (this.ccEmails && !this.isOnPrivateNote) {
        messagePayload.ccEmails = this.ccEmails;
      }

      if (this.bccEmails && !this.isOnPrivateNote) {
        messagePayload.bccEmails = this.bccEmails;
      }

      if (this.toEmails && !this.isOnPrivateNote) {
        messagePayload.toEmails = this.toEmails;
      }
      return messagePayload;
    },
    setCcEmails(value) {
      this.bccEmails = value.bccEmails;
      this.ccEmails = value.ccEmails;
    },
    setCCAndToEmailsFromLastChat() {
      const conversationContact = this.currentChat?.meta?.sender?.email || '';
      const { email: inboxEmail, forward_to_email: forwardToEmail } =
        this.inbox;

      const { cc, bcc, to } = getRecipients(
        this.lastEmail,
        conversationContact,
        inboxEmail,
        forwardToEmail
      );

      this.toEmails = to.join(', ');
      this.ccEmails = cc.join(', ');
      this.bccEmails = bcc.join(', ');
    },
    fetchAndSetReplyTo() {
      const replyStorageKey = LOCAL_STORAGE_KEYS.MESSAGE_REPLY_TO;
      const replyToMessageId = LocalStorage.getFromJsonStore(
        replyStorageKey,
        this.conversationId
      );

      this.inReplyTo = this.currentChat?.messages?.find(message => {
        if (message.id === replyToMessageId) {
          return true;
        }
        return false;
      });
    },
    onReplyToMessage() {
      this.fetchAndSetReplyTo();
      if (this.inReplyTo) {
        this.$nextTick(() => {
          const pos = this.isSignatureEnabledForInbox ? 'start' : 'end';
          this.messageEditor?.focusEditorInputField(pos);
        });
      }
    },
    resetReplyToMessage() {
      const replyStorageKey = LOCAL_STORAGE_KEYS.MESSAGE_REPLY_TO;
      LocalStorage.deleteFromJsonStore(replyStorageKey, this.conversationId);
      emitter.emit(BUS_EVENTS.TOGGLE_REPLY_TO_MESSAGE);
    },
    onNewConversationModalActive(isActive) {
      // Issue is if the new conversation modal is open and we drag and drop the file
      // then the file is not getting attached to the new conversation modal
      // and it is getting attached to the current conversation reply box
      // so to fix this we are removing the drag and drop event listener from the current conversation reply box
      // When new conversation modal is open
      this.newConversationModalActive = isActive;
    },
    onSearchPopoverClose() {
      this.showArticleSearchPopover = false;
    },
    toggleInsertArticle() {
      this.showArticleSearchPopover = !this.showArticleSearchPopover;
    },
    resetAudioRecorderInput() {
      this.recordingAudioDurationText = '00:00';
      this.isRecordingAudio = false;
      this.recordingAudioState = '';
      this.hasRecordedAudio = false;
      // Only clear the recorded audio when we click toggle button.
      this.attachedFiles = this.attachedFiles.filter(
        file => !file?.isRecordedAudio
      );
    },
    attachCannedFiles(cannedAttachments) {
      if (!cannedAttachments || !cannedAttachments.length) return;
      cannedAttachments.forEach(attachment => {
        fetch(attachment.file_url)
          .then(response => response.blob())
          .then(blob => {
            const file = new File([blob], attachment.filename, {
              type: attachment.file_type,
            });
            this.onFileUpload({ file, type: file.type });
          })
          .catch(() => {
            // Silently skip failed attachment downloads
          });
      });
    },
    togglePopout() {
      this.$emit('update:popOutReplyBox', !this.popOutReplyBox);
    },
    toggleEditorSize() {
      this.$emit('toggleEditorSize');
      this.$nextTick(() => this.messageEditor?.focusEditorInputField());
    },
    onSubmitCopilotReply() {
      const acceptedMessage = this.copilot.accept();
      if (acceptedMessage) {
        this.message = acceptedMessage;
        this.setCopilotAcceptedMessage(acceptedMessage);
      }
      this.preLumosMessage = '';
      this.lumosFollowUpInput = '';
    },
  },
};
</script>

<template>
  <ReplyBoxBanner :message="message" :is-on-private-note="isOnPrivateNote" />
  <Transition name="advup-altura">
    <div v-if="composerBlockedByAssignment" class="mx-2 mb-2">
      <Banner
        color-scheme="secondary"
        class="rounded-lg !py-2"
        :banner-message="
          $t('CONVERSATION.REPLYBOX.ASSIGNED_TO_OTHER_BANNER', {
            name: assignedAgent?.name,
          })
        "
        has-action-button
        action-button-variant="ghost"
        :action-button-label="$t('CONVERSATION.REPLYBOX.TRANSFER_TO_ME')"
        @primary-action="transferConversationToMe"
      />
      <button
        type="button"
        class="text-xs text-n-slate-11 hover:text-n-slate-12 underline ml-2 mt-1"
        @click="switchToPrivateNote"
      >
        {{ $t('CONVERSATION.REPLYBOX.PRIVATE_NOTE') }}
      </button>
    </div>
  </Transition>
  <div ref="replyEditor" class="reply-box" :class="replyBoxClass">
    <ReplyTopPanel
      :mode="replyType"
      :conversation-id="conversationId"
      :is-reply-restricted="isReplyRestricted"
      :disabled="copilot.isGenerating.value || showAudioRecorderEditor"
      :is-editor-disabled="isEditorDisabled"
      :is-message-length-reaching-threshold="isMessageLengthReachingThreshold"
      :characters-remaining="charactersRemaining"
      :editor-content="message"
      :show-follow-up="!isOnPrivateNote"
      :show-forward-contact="!isOnPrivateNote && !isEditorDisabled"
      @set-reply-mode="setReplyMode"
      @toggle-editor-size="toggleEditorSize"
      @toggle-copilot="copilot.toggleEditor"
      @execute-copilot-action="executeCopilotAction"
      @open-follow-up-modal="openFollowUpModal"
      @open-forward-contact-modal="openForwardContactModal"
      @open-assinatura="abrirAssinatura"
    />
    <ArticleSearchPopover
      v-if="showArticleSearchPopover && connectedPortalSlug"
      :selected-portal-slug="connectedPortalSlug"
      @insert="handleInsert"
      @close="onSearchPopoverClose"
    />
    <!-- WhatsApp-style pill layout (always shown) -->
    <div class="px-3 pb-2 pt-1">
      <!-- Banners do Lumos (a IA). Tom ÍRIS — no produto, íris é IA; o azul
           do Chatwoot (`woot-*`) que estava aqui é o tom da conversa. A
           transição é nomeada e lista o que anima: `transition-all` é
           proibido pelo padrão (anima até mudança de largura). -->
      <Transition name="rb-ia">
        <div
          v-if="copilot.isGenerating.value"
          class="rb-ia rb-ia--pensando"
          role="status"
        >
          <span class="i-lucide-sparkles rb-ia__ico" aria-hidden="true" />
          <span class="rb-ia__txt">
            {{ $t('CONVERSATION.REPLYBOX.COPILOT_THINKING') }}
          </span>
          <button type="button" class="rb-ia__acao" @click="copilot.reset()">
            {{ $t('GENERAL.CANCEL') }}
          </button>
        </div>
      </Transition>

      <Transition name="rb-ia">
        <div
          v-if="copilot.showEditor.value && !copilot.isGenerating.value"
          class="rb-ia rb-ia--pronta"
        >
          <div class="rb-ia__cab">
            <span class="i-lucide-sparkles rb-ia__ico" aria-hidden="true" />
            <span class="rb-ia__txt rb-ia__txt--forte">
              {{ $t('CONVERSATION.REPLYBOX.LUMOS_SUGGESTION_READY') }}
            </span>
            <button type="button" class="rb-ia__acao" @click="onLumosDiscard">
              {{ $t('GENERAL.DISCARD') }}
            </button>
          </div>
          <div class="rb-ia__pedido">
            <input
              v-model="lumosFollowUpInput"
              type="text"
              class="rb-ia__campo reset-base"
              :placeholder="$t('CONVERSATION.FOOTER.COPILOT_MSG_INPUT')"
              :aria-label="$t('CONVERSATION.FOOTER.COPILOT_MSG_INPUT')"
              @keydown.enter.prevent="onLumosFollowUp"
            />
            <button
              type="button"
              class="rb-ia__enviar"
              :aria-label="$t('CONVERSATION.FOOTER.COPILOT_MSG_INPUT')"
              :disabled="!lumosFollowUpInput.trim()"
              @click="onLumosFollowUp"
            >
              <span class="i-lucide-arrow-up" aria-hidden="true" />
            </button>
          </div>
        </div>
      </Transition>

      <!-- A janela de 24 h do WhatsApp oficial: âmbar acabando, rubi fechada. -->
      <Transition name="rb-ia">
        <JanelaDeResposta
          v-if="mostrarJanela"
          :janela="janela"
          :nome="currentChat?.meta?.sender?.name"
          :anuncio-gratis-ate="currentChat?.anuncio_gratis_ate || 0"
          @nota="switchToPrivateNote"
          @modelo="openWhatsappTemplateModal"
        />
      </Transition>

      <!-- Outer pill container -->
      <div class="rb-linha" :class="{ 'rb-linha--privada': isPrivate }">
        <!-- Esquerda: a lixeira quando há áudio gravado; senão, as ações. -->
        <button
          v-if="showAudioRecorderEditor"
          v-tooltip.top="$t('CONVERSATION.REPLYBOX.DISCARD_AUDIO')"
          type="button"
          class="advup-acao advup-acao--perigo rb-lixeira"
          :aria-label="$t('CONVERSATION.REPLYBOX.DISCARD_AUDIO')"
          @click="resetRecorderAndClearAttachments"
        >
          <i class="i-ph-trash" />
        </button>
        <ReplyBottomPanel
          v-else
          compact
          :conversation-id="conversationId"
          :enable-multiple-file-upload="enableMultipleFileUpload"
          :enable-whats-app-templates="showWhatsappTemplates"
          :enable-content-templates="showContentTemplates"
          :inbox="inbox"
          :is-on-private-note="isOnPrivateNote"
          :is-recording-audio="isRecordingAudio"
          :is-send-disabled="isReplyButtonDisabled"
          :is-note="isPrivate"
          :is-editor-disabled="isEditorDisabled"
          :on-file-upload="onFileUpload"
          :on-send="onSendReply"
          :conversation-type="conversationType"
          :recording-audio-duration-text="recordingAudioDurationText"
          :recording-audio-state="recordingAudioState"
          :send-button-text="replyButtonLabel"
          :show-audio-recorder="showAudioRecorder"
          :show-file-upload="showFileUpload"
          :show-quoted-reply-toggle="shouldShowQuotedReplyToggle"
          :quoted-reply-enabled="quotedReplyPreference"
          :toggle-audio-recorder-play-pause="toggleAudioRecorderPlayPause"
          :toggle-audio-recorder="toggleAudioRecorder"
          :toggle-emoji-picker="toggleEmojiPicker"
          :portal-slug="connectedPortalSlug"
          :new-conversation-modal-active="newConversationModalActive"
          @select-whatsapp-template="openWhatsappTemplateModal"
          @select-content-template="openContentTemplateModal"
          @toggle-insert-article="toggleInsertArticle"
          @toggle-quoted-reply="toggleQuotedReply"
          @open-follow-up-modal="openFollowUpModal"
          @open-forward-contact-modal="openForwardContactModal"
          @quick-reply-selected="content => (message = content)"
        />

        <!-- Center: white pill input -->
        <!-- O campo. Acende no foco (anel azul, como todo campo do produto),
             fica âmbar na nota privada — que NÃO vai para o cliente — e íris
             enquanto há sugestão da IA sobre ele. -->
        <div
          class="rb-campo"
          :class="{
            'rb-campo--privada': isPrivate,
            'rb-campo--ia':
              copilot.showEditor.value && !copilot.isGenerating.value,
          }"
        >
          <Transition name="advup-altura">
            <ReplyToMessage
              v-if="shouldShowReplyToMessage"
              :message="inReplyTo"
              @dismiss="resetReplyToMessage"
            />
          </Transition>
          <Transition name="advup-pop">
            <EmojiInput
              v-if="showEmojiPicker"
              v-on-clickaway="hideEmojiPicker"
              class="rb-emoji advup-pop advup-pop--acima"
              :class="{ 'emoji-dialog--expanded': isOnExpandedLayout }"
              :on-click="addIntoEditor"
            />
          </Transition>
          <Transition name="advup-altura">
            <ReplyEmailHead
              v-if="showReplyHead && isDefaultEditorMode"
              v-model:cc-emails="ccEmails"
              v-model:bcc-emails="bccEmails"
              v-model:to-emails="toEmails"
            />
          </Transition>
          <AudioRecorder
            v-if="showAudioRecorderEditor"
            ref="audioRecorderInput"
            :audio-record-format="audioRecordFormat"
            :height="32"
            @recorder-progress-changed="onRecordProgressChanged"
            @finish-record="onFinishRecorder"
            @play="recordingAudioState = 'playing'"
            @pause="recordingAudioState = 'paused'"
          />
          <!-- Lumos generating: skeleton shimmer in editor area -->
          <div
            v-else-if="copilot.isGenerating.value"
            class="rb-esqueleto"
            aria-hidden="true"
          >
            <span class="rb-esqueleto__linha" />
            <span class="rb-esqueleto__linha rb-esqueleto__linha--curta" />
          </div>
          <WootMessageEditor
            v-else
            ref="messageEditor"
            v-model="message"
            :conversation-id="conversationId"
            :editor-id="editorStateId"
            class="input popover-prosemirror-menu"
            :is-private="isOnPrivateNote"
            :placeholder="messagePlaceHolder"
            :update-selection-with="updateEditorSelectionWith"
            :min-height="1"
            :disabled="isEditorDisabled"
            enable-variables
            :variables="messageVariables"
            :signature="messageSignature"
            allow-signature
            :channel-type="channelType"
            :medium="inbox.medium"
            @typing-off="onTypingOff"
            @typing-on="onTypingOn"
            @focus="onFocus"
            @blur="onBlur"
            @toggle-user-mention="toggleUserMention"
            @toggle-canned-menu="toggleCannedMenu"
            @toggle-variables-menu="toggleVariablesMenu"
            @clear-selection="clearEditorSelection"
            @execute-copilot-action="executeCopilotAction"
            @attach-canned-files="attachCannedFiles"
          />
          <QuotedEmailPreview
            v-if="shouldShowQuotedPreview && isDefaultEditorMode"
            :quoted-email-text="quotedEmailText"
            :preview-text="quotedEmailPreviewText"
            class="mb-1"
            @toggle="toggleQuotedReply"
          />
          <Transition name="advup-altura">
            <div
              v-if="hasAttachments && isDefaultEditorMode"
              class="bg-transparent py-0 mb-1"
              @paste="onPaste"
            >
              <AttachmentPreview
                class="mt-1"
                :attachments="attachedFiles"
                @remove-attachment="removeAttachment"
              />
            </div>
          </Transition>
          <Transition name="advup-altura">
            <MessageSignatureMissingAlert
              v-if="
                isSignatureEnabledForInbox &&
                !isSignatureAvailable &&
                isDefaultEditorMode
              "
              class="mb-1"
            />
          </Transition>
        </div>

        <!-- Right: mic (empty) / send (has content or audio) button -->
        <button
          v-tooltip.top="botaoPrincipalRotulo"
          class="rb-enviar"
          :class="{ 'rb-enviar--privada': isPrivate }"
          :aria-label="botaoPrincipalRotulo"
          :disabled="
            isEditorDisabled ||
            (!showAudioRecorderEditor &&
              !botaoGrava &&
              (!hasMessageContent || isReplyButtonDisabled))
          "
          type="button"
          @click="
            showAudioRecorderEditor
              ? stopAndSendRecording()
              : botaoGrava
                ? toggleAudioRecorder()
                : onSendReply()
          "
        >
          <Transition name="rb-troca" mode="out-in">
            <span
              :key="botaoGrava ? 'gravar' : 'enviar'"
              :class="
                botaoGrava
                  ? 'i-ph-microphone-fill'
                  : 'i-ph-paper-plane-tilt-fill'
              "
              class="rb-enviar__ico"
            />
          </Transition>
        </button>
      </div>
    </div>

    <PlaceInFollowUpModal
      :show="showFollowUpModal"
      :conversation-id="currentChat.id"
      @close="closeFollowUpModal"
    />

    <ForwardContactModal
      :show="showForwardContactModal"
      :conversation-id="currentChat.id"
      @close="closeForwardContactModal"
    />

    <!-- Sai pelo mesmo caminho por onde entrou (24/10/2026, `_advup-modal.scss`). -->
    <Transition name="advup-saida">
      <EnviarModelo
        v-if="mostrarEnviarModelo"
        :conversa="currentChat"
        @close="mostrarEnviarModelo = false"
      />
    </Transition>

    <WhatsappTemplates
      :inbox-id="inbox.id"
      :show="showWhatsAppTemplatesModal"
      @close="hideWhatsappTemplatesModal"
      @on-send="onSendWhatsAppReply"
      @cancel="hideWhatsappTemplatesModal"
    />

    <ContentTemplates
      :inbox-id="inbox.id"
      :show="showContentTemplatesModal"
      @close="hideContentTemplatesModal"
      @on-send="onSendContentTemplateReply"
      @cancel="hideContentTemplatesModal"
    />

    <woot-confirm-modal
      ref="confirmDialog"
      :title="$t('CONVERSATION.REPLYBOX.UNDEFINED_VARIABLES.TITLE')"
      :description="undefinedVariableMessage"
    />
  </div>
</template>

<style scoped>
/* ── A pílula de escrever ────────────────────────────────────────────────
   Ações à esquerda, campo no meio, enviar/gravar à direita — o desenho dos
   mensageiros, que é o que a mão já conhece. */
.rb-linha {
  display: flex;
  align-items: flex-end;
  gap: var(--esp-1);
  padding: var(--esp-1) var(--esp-2);
  border: 1px solid rgb(var(--slate-4));
  border-radius: var(--r-5);
  background: rgb(var(--slate-2));
  transition:
    background-color var(--mov-2) var(--mov-curva),
    border-color var(--mov-2) var(--mov-curva);
}

/* Nota privada: a pílula INTEIRA fica âmbar. Escrever para a equipe achando
   que é para o cliente (ou o contrário) é o erro mais caro desta tela. */
.rb-linha--privada {
  border-color: rgb(var(--amber-6));
  background: rgb(var(--amber-2));
}

.rb-lixeira {
  align-self: center;
}

/* ── O campo ─────────────────────────────────────────────────────────── */
.rb-campo {
  display: flex;
  flex: 1;
  flex-direction: column;
  justify-content: center;
  min-width: 0;
  min-height: 36px;
  /* 🔴 SÓ O EDITOR ROLA (Victor, 16/09/2026: "por que dois scrolls?").
     A pílula também tinha teto de 8rem e rolagem própria; o editor dentro
     dela tem o mesmo teto e rola, e com as margens passava 4px da pílula —
     duas barras lado a lado. A pílula agora só recorta. */
  overflow: hidden;
  margin-inline: var(--esp-1);
  padding: var(--esp-1) var(--esp-3);
  border: 1px solid rgb(var(--slate-5));
  border-radius: var(--r-4);
  background: rgb(var(--solid-1));
  transition:
    border-color var(--mov-1) var(--mov-curva),
    box-shadow var(--mov-2) var(--mov-curva),
    background-color var(--mov-2) var(--mov-curva);
}

/* O campo ACENDE quando recebe o cursor — o mesmo anel de todo campo do
   produto. Numa tela onde se escreve o dia inteiro, é o que diz "pode
   digitar". */
/* 🔴 O PARÁGRAFO DO EDITOR TRAZIA MARGEM PRÓPRIA (10px em cima, 8px embaixo)
   — medido em 16/09/2026. Com uma linha só, o campo tinha 51px e os botões
   ao lado (32 e 36px) ficavam 10px abaixo do centro dele (Victor: "emoji,
   respostas rápidas, arquivo e o áudio não estão centralizados"). O campo
   de uma linha agora mede os mesmos 36px do botão de enviar. */
.rb-campo :deep(.ProseMirror) {
  scrollbar-width: thin;
  scrollbar-color: rgb(var(--slate-6)) transparent;
}

.rb-campo :deep(.ProseMirror > :first-child) {
  margin-top: 0;
}

.rb-campo :deep(.ProseMirror > :last-child) {
  margin-bottom: 0;
}

/* O texto de ajuda ("Shift + enter para nova linha…") é longo: numa coluna
   estreita ele quebrava em duas linhas, a segunda ficava escondida e o campo
   mostrava uma barra de rolagem com uma linha só (medido: 46px de ajuda num
   parágrafo de 23). Uma linha, com reticências. */
.rb-campo :deep(p.empty-node:first-child) {
  position: relative;
}

.rb-campo :deep(p.empty-node:first-child::before) {
  right: 0;
  left: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.rb-campo:focus-within {
  border-color: rgb(var(--blue-8));
  box-shadow: 0 0 0 3px rgb(var(--blue-9) / 15%);
}

.rb-campo--privada {
  border-color: rgb(var(--amber-6));
  background: rgb(var(--amber-2));
}

.rb-campo--privada:focus-within {
  border-color: rgb(var(--amber-8));
  box-shadow: 0 0 0 3px rgb(var(--amber-9) / 18%);
}

.rb-campo--ia {
  border-color: rgb(var(--iris-7));
}

/* ── Enviar / gravar ────────────────────────────────────────────────────
   Era `bg-[#1e2d4d]` — um navy fora da paleta e que não mudava de tema. É o
   botão mais clicado da tela: é ele que manda a mensagem para o cliente. */
.rb-enviar {
  display: grid;
  place-items: center;
  width: 36px;
  height: 36px;
  flex-shrink: 0;
  border: 0;
  border-radius: var(--r-redondo);
  background: rgb(var(--blue-9));
  color: rgb(255 255 255);
  box-shadow: 0 4px 12px -4px rgb(var(--blue-9) / 60%);
  cursor: pointer;
  transition:
    background-color var(--mov-1) var(--mov-curva),
    scale var(--mov-2) var(--mov-curva-pulo),
    box-shadow var(--mov-2) var(--mov-curva);
}

.rb-enviar:hover:not(:disabled) {
  background: rgb(var(--blue-10));
  scale: 1.06;
}

/* Na nota privada o botão acompanha a pílula: salvar uma nota para a equipe
   não pode ter a cara de mandar uma mensagem ao cliente. */
.rb-enviar--privada {
  background: rgb(var(--amber-9));
  color: rgb(var(--amber-12));
  box-shadow: 0 4px 12px -4px rgb(var(--amber-9) / 60%);
}

.rb-enviar--privada:hover:not(:disabled) {
  background: rgb(var(--amber-10));
}

.rb-enviar:active:not(:disabled) {
  scale: 0.93;
}

.rb-enviar:focus-visible {
  outline: 2px solid rgb(var(--blue-9));
  outline-offset: 2px;
}

.rb-enviar:disabled {
  opacity: 0.4;
  box-shadow: none;
  cursor: not-allowed;
}

.rb-enviar__ico {
  display: block;
  width: 18px;
  height: 18px;
}

/* Trocar de microfone para avião de papel é o sinal de que a mensagem está
   pronta: o ícone gira de leve e cresce, em vez de trocar de estalo. */
.rb-troca-enter-active,
.rb-troca-leave-active {
  transition:
    opacity var(--mov-1) var(--mov-curva),
    rotate var(--mov-2) var(--mov-curva),
    scale var(--mov-2) var(--mov-curva-pulo);
}

.rb-troca-enter-from {
  opacity: 0;
  rotate: -45deg;
  scale: 0.6;
}

.rb-troca-leave-to {
  opacity: 0;
  scale: 0.6;
}

/* ── A IA (Lumos) ──────────────────────────────────────────────────────── */
.rb-ia {
  overflow: hidden;
  margin-bottom: var(--esp-2);
  border: 1px solid rgb(var(--iris-6));
  border-radius: var(--r-3);
  background: rgb(var(--iris-2));
}

.rb-ia--pensando,
.rb-ia__cab {
  display: flex;
  align-items: center;
  gap: var(--esp-2);
  padding: var(--esp-2) var(--esp-3);
}

.rb-ia__ico {
  width: var(--ico-1);
  height: var(--ico-1);
  flex-shrink: 0;
  color: rgb(var(--iris-11));
}

.rb-ia--pensando .rb-ia__ico {
  animation: rb-brilha calc(var(--mov-3) * 5) var(--mov-curva) infinite;
}

@keyframes rb-brilha {
  50% {
    opacity: 0.35;
    scale: 0.85;
  }
}

.rb-ia__txt {
  flex: 1;
  min-width: 0;
  font-size: var(--txt-nota);
  color: rgb(var(--iris-11));
}

.rb-ia__txt--forte {
  font-weight: var(--peso-forte);
}

.rb-ia__acao {
  padding: var(--esp-1) var(--esp-2);
  border: 0;
  border-radius: var(--r-2);
  background: none;
  font-size: var(--txt-nota);
  font-weight: var(--peso-rotulo);
  color: rgb(var(--slate-11));
  cursor: pointer;
  transition:
    background-color var(--mov-1) var(--mov-curva),
    color var(--mov-1) var(--mov-curva);
}

.rb-ia__acao:hover {
  background: rgb(var(--iris-4));
  color: rgb(var(--slate-12));
}

.rb-ia__pedido {
  display: flex;
  align-items: center;
  gap: var(--esp-2);
  padding: var(--esp-2) var(--esp-3);
  border-top: 1px solid rgb(var(--iris-5));
  background: rgb(var(--solid-1));
}

.rb-ia__campo {
  flex: 1;
  min-width: 0;
  border: 0;
  background: transparent;
  font-size: var(--txt-nota);
  color: rgb(var(--slate-12));
  outline: none;
}

.rb-ia__campo::placeholder {
  color: rgb(var(--slate-10));
}

.rb-ia__enviar {
  display: grid;
  place-items: center;
  width: 24px;
  height: 24px;
  flex-shrink: 0;
  border: 0;
  border-radius: var(--r-redondo);
  background: rgb(var(--iris-9));
  color: rgb(255 255 255);
  cursor: pointer;
  transition: opacity var(--mov-1) var(--mov-curva);
}

.rb-ia__enviar:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.rb-ia__enviar > span {
  width: var(--ico-1);
  height: var(--ico-1);
}

.rb-ia-enter-active,
.rb-ia-leave-active {
  transition:
    opacity var(--mov-2) var(--mov-curva),
    translate var(--mov-2) var(--mov-curva);
}

.rb-ia-enter-from,
.rb-ia-leave-to {
  opacity: 0;
  translate: 0 -4px;
}

/* Enquanto a IA escreve, o campo mostra o esboço de duas linhas. */
.rb-esqueleto {
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: var(--esp-2);
  padding-block: var(--esp-1);
}

.rb-esqueleto__linha {
  display: block;
  width: 75%;
  height: 10px;
  border-radius: var(--r-1);
  background: linear-gradient(
    90deg,
    rgb(var(--iris-3)),
    rgb(var(--iris-5)),
    rgb(var(--iris-3))
  );
  background-size: 200% 100%;
  animation: rb-varre calc(var(--mov-3) * 4) linear infinite;
}

.rb-esqueleto__linha--curta {
  width: 50%;
}

@keyframes rb-varre {
  to {
    background-position: -200% 0;
  }
}

@media (prefers-reduced-motion: reduce) {
  .rb-enviar,
  .rb-enviar:hover:not(:disabled),
  .rb-enviar:active:not(:disabled) {
    transition: none;
    scale: 1;
  }

  .rb-ia--pensando .rb-ia__ico,
  .rb-esqueleto__linha {
    animation: none;
  }
}
</style>

<style lang="scss" scoped>
.reply-box {
  @apply relative mb-2 mx-2 border border-n-weak rounded-xl bg-n-solid-1;

  &.is-private {
    @apply bg-n-solid-amber dark:border-n-amber-3/10 border-n-amber-12/5;
  }

  // ⚠️ Os `!important` daqui são a EXCEÇÃO registrada: o editor é do
  // Chatwoot (upstream) e só aceita altura por `--editor-*-height` num clamp
  // em que mínimo e máximo viram a mesma medida — o campo ficaria de altura
  // fixa. O compacto precisa de um campo que CRESÇA com o texto (1,25 a
  // 8rem), e isso só existe vencendo a regra dele. Trocar por variável exige
  // mexer no ResizableEditorWrapper, que é o caminho do envio.
  &.is-compact {
    @apply border-transparent bg-transparent rounded-none;

    :deep(.ProseMirror-woot-style) {
      min-height: 1.25rem !important;
      max-height: 8rem !important;
      overflow-y: auto;
    }

    :deep(.input) {
      padding: 0 !important;
      border: none !important;
      box-shadow: none !important;
      background: transparent !important;
      min-height: 0 !important;
    }
  }
}

/* O seletor de emoji abre ACIMA do botão, alinhado à esquerda (igual ao
   chat interno). A classe vem do USO (`class="rb-emoji"`), e não da raiz do
   componente: uma regra escrita contra a classe interna dele parecia morta
   para o verificador de CSS e quase foi apagada — e aí o seletor voltava a
   abrir por cima do campo. */
.rb-emoji {
  @apply top-[unset] bottom-full mb-2 ltr:left-0 ltr:right-[unset] rtl:right-0 rtl:left-[unset];

  &::before {
    @apply hidden;
  }
}

.emoji-dialog--expanded {
  @apply left-[unset] bottom-0 absolute z-[100];

  &::before {
    transform: rotate(0deg);
    @apply ltr:left-1 rtl:right-1 -bottom-2;
  }
}

/* ── Celular ─────────────────────────────────────────────────────────────────
   Medido em 08/09/2026 numa conversa real, em 375px: a barra tinha 333px
   divididos em 100px de ícones + 175px de campo + 34px de enviar. Escrever
   numa faixa de 175px é o que fazia até o texto de ajuda quebrar em duas
   linhas e sair cortado.

   No celular a barra passa a ter duas fileiras: o campo e o enviar em cima,
   os ícones de ação embaixo. O campo ganha a largura inteira, que é o que a
   pessoa usa 100% das vezes; os ícones continuam todos ali, a um toque, e
   ninguém perde função. Acima de 640px a barra segue numa fileira só. */
@media (max-width: 639px) {
  .rb-linha {
    flex-wrap: wrap;
    row-gap: var(--esp-1);
  }

  .rb-linha > :first-child {
    order: 2;
    flex-basis: 100%;
  }
}
</style>
